import { apiClient } from './api';

// Proton Calendar API endpoints
export const protonCalendarAPI = {
  // Get all calendar events for a date range
  getEvents: async (params = {}) => {
    const response = await apiClient.get('/calendar/events', { params });
    return response.data;
  },

  // Get events for a specific date (View More functionality)
  getEventsByDate: async (date, params = {}) => {
    const response = await apiClient.get(`/calendar/events/date/${date}`, { params });
    return response.data;
  },

  // Get upcoming events
  getUpcomingEvents: async (params = {}) => {
    const response = await apiClient.get('/calendar/events/upcoming', { params });
    return response.data;
  },

  // Get calendar statistics
  getCalendarStats: async (params = {}) => {
    const response = await apiClient.get('/calendar/stats', { params });
    return response.data;
  },

  // Get holidays only
  getHolidays: async (params = {}) => {
    const response = await apiClient.get('/calendar/holidays', { params });
    return response.data;
  },

  // Get exams only
  getExams: async (params = {}) => {
    const response = await apiClient.get('/calendar/exams', { params });
    return response.data;
  },

  // Search calendar events
  searchEvents: async (query, params = {}) => {
    const response = await apiClient.get('/calendar/search', {
      params: {
        search: query,
        ...params
      }
    });
    return response.data;
  },

  // Create new event/meeting
  createEvent: async (eventData) => {
    const response = await apiClient.post('/calendar/events', eventData);
    return response.data;
  },

  // Get detailed event information
  getEventDetails: async (eventId) => {
    const response = await apiClient.get(`/calendar/events/${eventId}/details`);
    return response.data;
  },

  // Update existing event
  updateEvent: async (eventId, eventData) => {
    const response = await apiClient.put(`/calendar/events/${eventId}`, eventData);
    return response.data;
  },

  // Delete event
  deleteEvent: async (eventId) => {
    const response = await apiClient.delete(`/calendar/events/${eventId}`);
    return response.data;
  },

  // Get assignment options with caching support
  getAssignmentOptions: async (type, params = {}) => {
    const response = await apiClient.get('/calendar/assignment-options', { 
      params: {
        type,
        ...params
      }
    });
    return response.data;
  },

  // Specific assignment option methods for better caching
  getClasses: async (params = {}) => {
    const response = await apiClient.get('/calendar/assignment-options', {
      params: {
        type: 'classes',
        ...params
      }
    });
    return response.data;
  },

  getRoles: async (params = {}) => {
    const response = await apiClient.get('/calendar/assignment-options', {
      params: {
        type: 'roles',
        ...params
      }
    });
    return response.data;
  },

  getSections: async (params = {}) => {
    const response = await apiClient.get('/calendar/assignment-options', {
      params: {
        type: 'sections',
        ...params
      }
    });
    return response.data;
  },

  getUsers: async (params = {}) => {
    const response = await apiClient.get('/calendar/assignment-options', {
      params: {
        type: 'users',
        limit: 50,
        ...params
      }
    });
    return response.data;
  }
};

// Legacy calendar API (keeping for backward compatibility)
export const calendarAPI = {
  // Get all events
  getEvents: async (params = {}) => {
    const response = await apiClient.get('/calendar/events', { params });
    return response.data;
  },

  // Get events for a specific date range
  getEventsByDateRange: async (startDate, endDate, params = {}) => {
    const response = await apiClient.get('/calendar/events/range', {
      params: {
        start_date: startDate,
        end_date: endDate,
        ...params
      }
    });
    return response.data;
  },

  // Get events for a specific date
  getEventsByDate: async (date, params = {}) => {
    const response = await apiClient.get(`/calendar/events/date/${date}`, { params });
    return response.data;
  },

  // Create a new event
  createEvent: async (eventData) => {
    const response = await apiClient.post('/calendar/events', eventData);
    return response.data;
  },

  // Update an existing event
  updateEvent: async (eventId, eventData) => {
    const response = await apiClient.put(`/calendar/events/${eventId}`, eventData);
    return response.data;
  },

  // Delete an event
  deleteEvent: async (eventId) => {
    const response = await apiClient.delete(`/calendar/events/${eventId}`);
    return response.data;
  },

  // Get upcoming events
  getUpcomingEvents: async (limit = 10, params = {}) => {
    const response = await apiClient.get('/calendar/events/upcoming', {
      params: {
        limit,
        ...params
      }
    });
    return response.data;
  },

  // Get events by type
  getEventsByType: async (eventType, params = {}) => {
    const response = await apiClient.get(`/calendar/events/type/${eventType}`, { params });
    return response.data;
  },

  // Get calendar statistics
  getCalendarStats: async (params = {}) => {
    const response = await apiClient.get('/calendar/stats', { params });
    return response.data;
  },

  // Search events
  searchEvents: async (query, params = {}) => {
    const response = await apiClient.get('/calendar/events/search', {
      params: {
        q: query,
        ...params
      }
    });
    return response.data;
  },

  // Get holidays
  getHolidays: async (year, params = {}) => {
    const response = await apiClient.get('/calendar/holidays', {
      params: {
        year,
        ...params
      }
    });
    return response.data;
  },

  // Get academic calendar
  getAcademicCalendar: async (academicYear, params = {}) => {
    const response = await apiClient.get('/calendar/academic', {
      params: {
        academic_year: academicYear,
        ...params
      }
    });
    return response.data;
  },

  // Get examination schedule
  getExaminationSchedule: async (params = {}) => {
    const response = await apiClient.get('/calendar/examinations', { params });
    return response.data;
  },

  // Event registration
  registerForEvent: async (eventId, registrationData) => {
    const response = await apiClient.post(`/calendar/events/${eventId}/register`, registrationData);
    return response.data;
  },

  // Cancel event registration
  cancelEventRegistration: async (eventId, registrationId) => {
    const response = await apiClient.delete(`/calendar/events/${eventId}/register/${registrationId}`);
    return response.data;
  },

  // Get event attendees
  getEventAttendees: async (eventId, params = {}) => {
    const response = await apiClient.get(`/calendar/events/${eventId}/attendees`, { params });
    return response.data;
  },

  // Mark attendance for event
  markEventAttendance: async (eventId, attendanceData) => {
    const response = await apiClient.post(`/calendar/events/${eventId}/attendance`, attendanceData);
    return response.data;
  },

  // Get event attendance
  getEventAttendance: async (eventId, params = {}) => {
    const response = await apiClient.get(`/calendar/events/${eventId}/attendance`, { params });
    return response.data;
  },

  // Export calendar
  exportCalendar: async (format = 'ics', params = {}) => {
    const response = await apiClient.get('/calendar/export', {
      params: {
        format,
        ...params
      },
      responseType: 'blob'
    });
    return response;
  },

  // Import calendar
  importCalendar: async (file, format = 'ics') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('format', format);

    const response = await apiClient.post('/calendar/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Get event templates
  getEventTemplates: async (params = {}) => {
    const response = await apiClient.get('/calendar/templates', { params });
    return response.data;
  },

  // Create event from template
  createEventFromTemplate: async (templateId, eventData) => {
    const response = await apiClient.post(`/calendar/templates/${templateId}/create`, eventData);
    return response.data;
  },

  // Get event reminders
  getEventReminders: async (params = {}) => {
    const response = await apiClient.get('/calendar/reminders', { params });
    return response.data;
  },

  // Set event reminder
  setEventReminder: async (eventId, reminderData) => {
    const response = await apiClient.post(`/calendar/events/${eventId}/reminders`, reminderData);
    return response.data;
  },

  // Get calendar permissions
  getCalendarPermissions: async (params = {}) => {
    const response = await apiClient.get('/calendar/permissions', { params });
    return response.data;
  },

  // Share calendar
  shareCalendar: async (shareData) => {
    const response = await apiClient.post('/calendar/share', shareData);
    return response.data;
  },

  // Get shared calendars
  getSharedCalendars: async (params = {}) => {
    const response = await apiClient.get('/calendar/shared', { params });
    return response.data;
  },

  // Sync with external calendar
  syncExternalCalendar: async (syncData) => {
    const response = await apiClient.post('/calendar/sync', syncData);
    return response.data;
  },

  // Get calendar settings
  getCalendarSettings: async () => {
    const response = await apiClient.get('/calendar/settings');
    return response.data;
  },

  // Update calendar settings
  updateCalendarSettings: async (settings) => {
    const response = await apiClient.put('/calendar/settings', settings);
    return response.data;
  }
};

// Calendar utility functions
export const calendarUtils = {
  // Format date for display
  formatEventDate: (date, includeTime = false) => {
    const eventDate = new Date(date);
    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };

    if (includeTime) {
      options.hour = '2-digit';
      options.minute = '2-digit';
    }

    return eventDate.toLocaleDateString('en-US', options);
  },

  // Format time range
  formatTimeRange: (startTime, endTime) => {
    if (!startTime && !endTime) return '';
    
    const formatTime = (time) => {
      if (!time) return '';
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    };

    if (startTime && endTime) {
      return `${formatTime(startTime)} - ${formatTime(endTime)}`;
    } else if (startTime) {
      return `From ${formatTime(startTime)}`;
    } else if (endTime) {
      return `Until ${formatTime(endTime)}`;
    }

    return '';
  },

  // Get event duration in minutes
  getEventDuration: (startTime, endTime) => {
    if (!startTime || !endTime) return null;

    const start = new Date(`1970-01-01T${startTime}:00`);
    const end = new Date(`1970-01-01T${endTime}:00`);
    
    return Math.round((end - start) / (1000 * 60));
  },

  // Check if event is today
  isEventToday: (eventDate) => {
    const today = new Date();
    const event = new Date(eventDate);
    
    return today.getDate() === event.getDate() &&
           today.getMonth() === event.getMonth() &&
           today.getFullYear() === event.getFullYear();
  },

  // Check if event is in the past
  isEventPast: (eventDate, eventTime = null) => {
    const now = new Date();
    const event = new Date(eventDate);
    
    if (eventTime) {
      const [hours, minutes] = eventTime.split(':');
      event.setHours(parseInt(hours), parseInt(minutes));
    } else {
      event.setHours(23, 59, 59); // End of day if no time specified
    }
    
    return event < now;
  },

  // Check if event is upcoming (within next 7 days)
  isEventUpcoming: (eventDate) => {
    const today = new Date();
    const event = new Date(eventDate);
    const nextWeek = new Date();
    nextWeek.setDate(today.getDate() + 7);
    
    return event >= today && event <= nextWeek;
  },

  // Get day name
  getDayName: (date) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[new Date(date).getDay()];
  },

  // Get month name
  getMonthName: (monthIndex) => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[monthIndex];
  },

  // Generate calendar weeks for a month
  getCalendarWeeks: (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());
    
    const weeks = [];
    const currentDate = new Date(startDate);
    
    while (currentDate <= lastDay || weeks.length < 6) {
      const week = [];
      for (let i = 0; i < 7; i++) {
        week.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      weeks.push(week);
      
      if (weeks.length === 6) break;
    }
    
    return weeks;
  },

  // Filter events by criteria
  filterEvents: (events, criteria) => {
    return events.filter(event => {
      // Filter by type
      if (criteria.types && criteria.types.length > 0) {
        if (!criteria.types.includes(event.type)) return false;
      }
      
      // Filter by date range
      if (criteria.startDate && criteria.endDate) {
        const eventDate = new Date(event.date);
        const start = new Date(criteria.startDate);
        const end = new Date(criteria.endDate);
        if (eventDate < start || eventDate > end) return false;
      }
      
      // Filter by search term
      if (criteria.search) {
        const searchTerm = criteria.search.toLowerCase();
        const titleMatch = event.title.toLowerCase().includes(searchTerm);
        const descriptionMatch = event.description?.toLowerCase().includes(searchTerm);
        const locationMatch = event.location?.toLowerCase().includes(searchTerm);
        
        if (!titleMatch && !descriptionMatch && !locationMatch) return false;
      }
      
      // Filter by importance
      if (criteria.importantOnly) {
        if (!event.isImportant) return false;
      }
      
      return true;
    });
  },

  // Sort events
  sortEvents: (events, sortBy = 'date', sortOrder = 'asc') => {
    return [...events].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.date);
          bValue = new Date(b.date);
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        default:
          aValue = new Date(a.date);
          bValue = new Date(b.date);
      }
      
      if (sortOrder === 'desc') {
        return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
      } else {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      }
    });
  }
};

export default calendarAPI;
